import { Controller } from '@hotwired/stimulus';
import {
  MARKER,
  convertExistingTokens,
  encodeSequence,
  escapeHtml as escapeHtmlUtil,
  replacePlaceholdersWithTokens
} from '../utils/mention_tokens';

export default class extends Controller {
  static values = {
    url: String
  };

  connect() {
    this.mentionMap = new Map();
    this.sequenceRef = { value: 0 };
    this.suggestions = [];
    this.activeIndex = -1;
    this.boundDocumentClick = this.handleDocumentClick.bind(this);

    this.editorElement = document.querySelector(`trix-editor[input="${this.element.id}"]`);

    if (this.editorElement) {
      if (this.editorElement.editor) {
        this.initializeEditor();
      } else {
        this.initializeEditorBound = this.initializeEditor.bind(this);
        this.editorElement.addEventListener('trix-initialize', this.initializeEditorBound, { once: true });
      }
    }

    if (this.element.form) {
      this.beforeSubmitHandler = this.beforeSubmit.bind(this);
      this.element.form.addEventListener('submit', this.beforeSubmitHandler);
    }
  }

  disconnect() {
    if (this.element.form && this.beforeSubmitHandler) {
      this.element.form.removeEventListener('submit', this.beforeSubmitHandler);
    }

    document.removeEventListener('click', this.boundDocumentClick);
    this.teardownEditor();
  }

  initializeEditor() {
    this.editor = this.editorElement.editor;
    this.setupSuggestionsElement();
    this.normalizeExistingValue();

    this.boundInputHandler = this.handleEditorInput.bind(this);
    this.boundKeydownHandler = this.handleKeydown.bind(this);
    this.editorElement.addEventListener('input', this.boundInputHandler);
    this.editorElement.addEventListener('keydown', this.boundKeydownHandler);
    document.addEventListener('click', this.boundDocumentClick);
  }

  teardownEditor() {
    if (!this.editorElement) {
      return;
    }

    this.editorElement.removeEventListener('input', this.boundInputHandler);
    this.editorElement.removeEventListener('keydown', this.boundKeydownHandler);

    if (this.initializeEditorBound) {
      this.editorElement.removeEventListener('trix-initialize', this.initializeEditorBound);
    }

    if (this.suggestionsElement?.parentNode) {
      if (this.boundSuggestionClick) {
        this.suggestionsElement.removeEventListener('click', this.boundSuggestionClick);
        this.boundSuggestionClick = null;
      }
      this.suggestionsElement.parentNode.removeChild(this.suggestionsElement);
    }
  }

  handleEditorInput() {
    this.pruneMentionMap();
    this.observeQuery();
  }

  handleKeydown(event) {
    if (!this.isOpen()) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.chooseActiveSuggestion();
        break;
      case 'Tab':
        this.chooseActiveSuggestion();
        break;
      case 'Escape':
        this.hideSuggestions();
        break;
      default:
    }
  }

  handleSuggestionClick(event) {
    const button = event.target.closest('[data-index]');
    if (!button) {
      return;
    }
    event.preventDefault();
    const index = Number.parseInt(button.dataset.index, 10);
    this.insertMentionFromIndex(index);
  }

  handleDocumentClick(event) {
    if (!this.editorElement) {
      return;
    }

    if (!this.editorElement.contains(event.target) && !this.suggestionsElement.contains(event.target)) {
      this.hideSuggestions();
    }
  }

  beforeSubmit() {
    const processed = replacePlaceholdersWithTokens(this.element.value, this.mentionMap);
    this.element.value = processed;
  }

  normalizeExistingValue() {
    const html = this.element.value || '';
    const converted = convertExistingTokens(html, this.mentionMap, this.sequenceRef);
    if (converted !== html) {
      this.element.value = converted;
      this.editor.loadHTML(converted);
    }
  }

  observeQuery() {
    if (!this.editor) {
      return;
    }

    const cursor = this.editor.getSelectedRange()[0];
    const documentText = this.editor.getDocument().toString();
    const textBefore = documentText.slice(0, cursor);
    const match = textBefore.match(/(?:^|\s)@([^\s@]*)$/);

    if (!match) {
      this.hideSuggestions();
      return;
    }

    const query = match[1];
    const start = cursor - query.length - 1;
    this.activeMentionRange = { start, end: cursor };

    this.scheduleFetch(query);
  }

  scheduleFetch(query) {
    this.clearFetchTimer();

    const trimmed = query.trim();
    this.fetchTimer = window.setTimeout(() => {
      this.performFetch(trimmed);
    }, 150);
  }

  clearFetchTimer() {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer);
      this.fetchTimer = null;
    }
  }

  performFetch(query) {
    if (!this.hasUrlValue) {
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    const url = new URL(this.urlValue, window.location.origin);
    if (query) {
      url.searchParams.set('q', query);
    }

    fetch(url.toString(), {
      signal: this.abortController.signal,
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.json())
      .then((data) => {
        this.renderSuggestions(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Mention fetch failed', error);
        }
      });
  }

  renderSuggestions(users) {
    this.suggestions = users;
    this.activeIndex = users.length ? 0 : -1;

    if (!users.length) {
      this.hideSuggestions();
      return;
    }

    const items = users
      .map((user, index) => `
        <button type="button" class="mention-suggestion" data-index="${index}">
          <span class="mention-suggestion__name">${escapeHtmlUtil(user.name || 'Unknown User')}</span>
          <span class="mention-suggestion__email">${escapeHtmlUtil(user.email)}</span>
        </button>
      `)
      .join('');

    this.suggestionsElement.innerHTML = items;
    this.suggestionsElement.classList.remove('hidden');
    this.highlightActive();

    if (!this.boundSuggestionClick) {
      this.boundSuggestionClick = this.handleSuggestionClick.bind(this);
      this.suggestionsElement.addEventListener('click', this.boundSuggestionClick);
    }
  }

  hideSuggestions() {
    if (!this.suggestionsElement) {
      return;
    }

    this.suggestionsElement.classList.add('hidden');
    this.suggestionsElement.innerHTML = '';
    this.suggestions = [];
    this.activeIndex = -1;
    this.activeMentionRange = null;
  }

  isOpen() {
    return this.suggestionsElement && !this.suggestionsElement.classList.contains('hidden');
  }

  moveSelection(direction) {
    if (!this.suggestions.length) {
      return;
    }

    const total = this.suggestions.length;
    this.activeIndex = (this.activeIndex + direction + total) % total;
    this.highlightActive();
  }

  highlightActive() {
    if (!this.suggestionsElement) {
      return;
    }

    Array.from(this.suggestionsElement.querySelectorAll('[data-index]')).forEach((button, index) => {
      if (index === this.activeIndex) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });
  }

  chooseActiveSuggestion() {
    if (this.activeIndex < 0 || this.activeIndex >= this.suggestions.length) {
      this.hideSuggestions();
      return;
    }

    this.insertMentionFromIndex(this.activeIndex);
  }

  insertMentionFromIndex(index) {
    const user = this.suggestions[index];
    if (!user || !this.activeMentionRange || !this.editor) {
      return;
    }

    const encoded = encodeSequence(this.sequenceRef.value++);
    const displayName = user.name || user.email || 'Unknown User';
    const mentionText = `${displayName}`;
    const placeholder = `${mentionText}${MARKER}${encoded}${MARKER} `;
    const { start, end } = this.activeMentionRange;

    this.editor.setSelectedRange([start, end - start]);
    this.editor.insertString(placeholder);

    this.mentionMap.set(encoded, { name: displayName, userId: Number.parseInt(user.id, 10) });
    this.hideSuggestions();
  }

  pruneMentionMap() {
    if (!this.editor || !this.mentionMap.size) {
      return;
    }

    const documentText = this.editor.getDocument().toString();
    Array.from(this.mentionMap.keys()).forEach((encoded) => {
      const marker = `${MARKER}${encoded}${MARKER}`;
      if (!documentText.includes(marker)) {
        this.mentionMap.delete(encoded);
      }
    });
  }

  setupSuggestionsElement() {
    if (!this.editorElement) {
      return;
    }

    const wrapper = this.editorElement.parentElement;
    if (wrapper) {
      wrapper.classList.add('mentionable-editor');
    }

    this.suggestionsElement = document.createElement('div');
    this.suggestionsElement.className = 'mention-suggestions hidden';

    if (wrapper) {
      wrapper.appendChild(this.suggestionsElement);
    } else {
      this.editorElement.insertAdjacentElement('afterend', this.suggestionsElement);
    }
  }
}
